import { useForm } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ChoiceGroup } from '@/components/ui/choice-group';
import partyPopper from '@/assets/party-popper.svg';
import { errorMessage } from '@/features/auth/use-auth-error';

import { asList, type StepProps } from './step-props';
import { useSaveStep } from './use-save-step';

interface ContactForm {
  preferredContactTime: string[] | false;
  preferredCommunication: string;
}

interface ContactStepProps extends StepProps {
  /** `eligible` is the congratulations page; `waiting` the off-ramp for screened-out applicants. */
  variant: 'eligible' | 'waiting';
}

export function ContactStep({ record, lookups, onNext, variant }: ContactStepProps) {
  const save = useSaveStep(variant === 'eligible' ? 'contact' : 'contact_waiting', onNext);
  const { register, handleSubmit } = useForm<ContactForm>({
    defaultValues: {
      preferredContactTime: record.preferredContactTime ?? [],
      preferredCommunication: record.preferredCommunication ?? '',
    },
  });

  const submit = (v: ContactForm) => {
    save.mutate({
      preferredContactTime: asList(v.preferredContactTime),
      preferredCommunication: v.preferredCommunication,
    });
  };

  return (
    <div className="stack stack--loose">
      {variant === 'eligible' ? (
        <div className="stack">
          <img src={partyPopper} alt="" className="art" />
          <h1>Congrats, {record.firstName}!</h1>
          <hr className="divider" />
          <h4>
            Based on the information you provided, you are eligible to receive an offer from MAS
            once you complete our credentialing process.
          </h4>
          <p>
            This includes verifying that you have all required professional licenses to perform the
            job, as well as a successful drug screen, COVID-19 vaccination or exemption, background
            and reference checks, and employment verification.
          </p>
          <hr className="divider divider--gray" />
          <h4>We will be in touch in the next 24 hours.</h4>
          <p>
            You&apos;ll be assigned a dedicated partner that will guide you through onboarding and
            be your go-to contact while on the job.
          </p>
        </div>
      ) : (
        <div className="stack">
          <p className="eyebrow">Thanks for your interest!</p>
          <h1>Let&apos;s set up a time to chat more...</h1>
          <hr className="divider" />
          <p>
            At MAS each candidate is assigned a dedicated point of contact to help you with the
            application process and beyond.
          </p>
        </div>
      )}

      {save.isError && <Alert>{errorMessage(save.error)}</Alert>}

      <form
        className="stack stack--loose"
        noValidate
        onSubmit={(e) => void handleSubmit(submit)(e)}
      >
        <ChoiceGroup
          type="checkbox"
          name="preferredContactTime"
          legend={
            variant === 'eligible'
              ? 'When is the best time to give you a call?'
              : 'When is the best time/s for one of our recruiters to give you a call?'
          }
          size="lg"
          inline
          options={lookups.preferredContactTime ?? []}
          registration={register('preferredContactTime')}
        />
        <ChoiceGroup
          type="radio"
          name="preferredCommunication"
          legend="And what is your preferred way for us to contact you?"
          size="lg"
          inline
          options={lookups.preferredCommMethod ?? []}
          registration={register('preferredCommunication')}
        />
        <Button type="submit" busy={save.isPending}>
          {variant === 'eligible' ? 'Finish up' : 'Save & continue'}
        </Button>
      </form>
    </div>
  );
}
